// Schritt 5 des Übergabe-Assistenten — Bemerkungen: freies Textfeld für
// Auffälligkeiten, ein Datum „zuletzt renoviert am" und ein Ja/Nein-Umschalter für
// „Nebenkosten laut Angabe bezahlt". Alle drei Felder sind optional — ein leeres
// Protokoll ohne Bemerkungen ist der Normalfall, kein Fehlerzustand.
//
// Das Renovierungsdatum wird als reines Datum (JJJJ-MM-TT, ohne Zeitanteil)
// gespeichert — die PDF-Finalisierung (supabase/functions/uebergabe-finalisieren)
// erwartet dieses Format und wandelt es dort selbst in TT.MM.JJJJ fürs Dokument um.
//
// Optik (design_handoff_uebergabe §4.5): Textarea-Widget, darunter zwei Widgets. Nebenkosten
// bleiben Ja | Nein wie bisher (Freigabe 17.10.2026: kein dritter Zustand „Keine Angabe“ —
// solange nichts gewählt ist, steht das Feld weiter auf null, nur zurück lässt es sich nicht setzen).
import React from 'react';
import { PaintRoller, Receipt } from '@phosphor-icons/react';
import type { LokalerEntwurf } from '../../../lib/uebergabe/lokal';
import { istTouch } from './assistent/Rundgangteile';

interface SchrittBemerkungenProps {
  entwurf: LokalerEntwurf;
  setEntwurf: (updater: LokalerEntwurf | ((vorher: LokalerEntwurf) => LokalerEntwurf)) => void;
}

const WIDGET = { borderRadius: 24, padding: '16px 18px', background: 'var(--glass)', boxShadow: 'var(--glass-sh)' } as const;

const SchrittBemerkungen: React.FC<SchrittBemerkungenProps> = ({ entwurf, setEntwurf }) => {
  const { protokoll } = entwurf;
  const touch = istTouch();

  const aendereBemerkungen = (wert: string) => setEntwurf(e => ({ ...e, protokoll: { ...e.protokoll, bemerkungen: wert || null } }));
  const aendereRenoviertAm = (wert: string) => setEntwurf(e => ({ ...e, protokoll: { ...e.protokoll, renoviert_am: wert || null } }));
  // Bei "Ja" ergibt eine Erklärung keinen Sinn mehr — die Notiz wird mitgelöscht, damit
  // nicht ein alter "Was ist offen?"-Text neben einem nachträglich auf "Ja" korrigierten
  // Wert stehen bleibt (Papierformular-Pendant: die Notiz gehört zusammen mit "Nein").
  const aendereNebenkosten = (wert: boolean) => setEntwurf(e => ({
    ...e, protokoll: { ...e.protokoll, nebenkosten_bezahlt: wert, nebenkosten_notiz: wert ? null : e.protokoll.nebenkosten_notiz },
  }));
  const aendereNebenkostenNotiz = (wert: string) => setEntwurf(e => ({ ...e, protokoll: { ...e.protokoll, nebenkosten_notiz: wert || null } }));

  const optionen = [
    { wert: true, label: 'Ja', farbe: '#34c759' },
    { wert: false, label: 'Nein', farbe: '#ff9500' },
  ] as const;

  return (
    <div className="flex flex-col" style={{ gap: 12 }}>
      <label className="flex flex-col" style={{ ...WIDGET, gap: 8 }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink3)' }}>Bemerkungen</span>
        <textarea
          value={protokoll.bemerkungen ?? ''}
          onChange={e => aendereBemerkungen(e.target.value)}
          placeholder="Sonstige Auffälligkeiten, Absprachen, Hinweise…"
          rows={5}
          style={{ width: '100%', resize: 'vertical', border: 'none', outline: 'none', borderRadius: 14, padding: '12px 14px', background: 'var(--fill)',
            boxShadow: 'inset 0 0 0 1px var(--line)', fontFamily: 'inherit', fontSize: 14.5, lineHeight: 1.5, color: 'var(--ink)' }}
        />
      </label>

      <div className="grid items-start" style={{ gap: 12, gridTemplateColumns: 'repeat(auto-fit, minmax(min(260px, 100%), 1fr))' }}>
        <label className="flex items-center" style={{ ...WIDGET, gap: 12 }}>
          <span aria-hidden className="inline-flex shrink-0 items-center justify-center" style={{ width: 36, height: 36, borderRadius: 11, background: '#ff9500', color: '#fff' }}>
            <PaintRoller size={18} weight="fill" />
          </span>
          <span className="flex min-w-0 flex-1 flex-col" style={{ gap: 2 }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink3)' }}>Zuletzt renoviert am</span>
            <input
              type="date"
              value={protokoll.renoviert_am ?? ''}
              onChange={e => aendereRenoviertAm(e.target.value)}
              style={{ minHeight: touch ? 44 : 28, border: 'none', outline: 'none', background: 'transparent', padding: 0, fontFamily: 'inherit',
                fontSize: 15, fontWeight: 700, color: 'var(--ink)', minWidth: 0 }}
            />
          </span>
        </label>

        <div className="flex flex-col" style={{ ...WIDGET, gap: 10 }}>
          <div className="flex items-center" style={{ gap: 12 }}>
            <span aria-hidden className="inline-flex shrink-0 items-center justify-center" style={{ width: 36, height: 36, borderRadius: 11, background: '#34c759', color: '#fff' }}>
              <Receipt size={18} weight="fill" />
            </span>
            <span id="nebenkosten-titel" className="flex-1" style={{ fontSize: 14, fontWeight: 800 }}>Nebenkosten laut Angabe bezahlt</span>
          </div>
          <div role="radiogroup" aria-labelledby="nebenkosten-titel" className="flex" style={{ padding: 3, borderRadius: 14, background: 'var(--fill2)' }}>
            {optionen.map(o => {
              const an = protokoll.nebenkosten_bezahlt === o.wert;
              return (
                <button key={o.label} type="button" role="radio" aria-checked={an} onClick={() => aendereNebenkosten(o.wert)}
                  className="flex-1" style={{ height: touch ? 44 : 36, border: 'none', borderRadius: 11, cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, fontWeight: 800,
                    background: an ? o.farbe : 'transparent', color: an ? '#fff' : 'var(--ink2)', boxShadow: an ? `0 4px 10px -4px ${o.farbe}` : 'none' }}>
                  {o.label}
                </button>
              );
            })}
          </div>
          {protokoll.nebenkosten_bezahlt === false && (
            <label className="flex flex-col" style={{ gap: 4, padding: '10px 12px', borderRadius: 14, background: 'var(--warn-soft)' }}>
              <span style={{ fontSize: 11.5, fontWeight: 800, color: 'var(--warn)' }}>Was ist offen?</span>
              <textarea
                value={protokoll.nebenkosten_notiz ?? ''}
                onChange={e => aendereNebenkostenNotiz(e.target.value)}
                placeholder="Welcher Betrag/Zeitraum ist offen, ggf. Absprache…"
                rows={3}
                style={{ border: 'none', outline: 'none', resize: 'vertical', background: 'transparent', fontFamily: 'inherit', fontSize: 14, lineHeight: 1.45, color: 'var(--ink)' }}
              />
            </label>
          )}
        </div>
      </div>
    </div>
  );
};

export default SchrittBemerkungen;
